import type { Topic } from '../QuestionTopics';
import { VOTES_PROPERTY_KEY, ACCEPTED_ANSWER_PROPERTY_KEY } from '../contentType';
import type {
	QuestionListQuery_search_nodes_content_children,
	QuestionListQuery_search_nodes_content_properties,
} from '../graphql/__types__/QuestionListQuery';

import type { Question } from './BaseQuestionsList';

// //delete the cache to load questions from server after deleting/creating questions
// //remove it when nextFetchPolicy from apollo 3 is available or alternatives
// //more info https://medium.com/@martinseanhunt/how-to-invalidate-cached-data-in-apollo-and-handle-updating-paginated-queries-379e4b9e4698

const deleteSpaceQuestionsCache = (cache: any) => {
	Object.keys(cache.data?.data || []).forEach(
		(key) => key.includes('confluence.questionsForSpace') && cache.data.delete(key),
	);
};

const deleteGlobalQuestionsCache = (cache: any) => {
	Object.keys(cache.data?.data || []).forEach(
		(key) => key.includes('search') && cache.data.delete(key),
	);
};

const deleteExpertsCache = (cache: any) => {
	Object.keys(cache.data?.data || []).forEach(
		(key) =>
			(key.includes('confluence_expertsOfAllTime') ||
				key.includes('confluence_expertsByTopic') ||
				key.includes('confluence_expertsOfWeek') ||
				key.includes('confluence_expert')) &&
			cache.data.delete(key),
	);
};

export const deleteQuestionListCache = (cache: any) => {
	deleteSpaceQuestionsCache(cache);
	deleteGlobalQuestionsCache(cache);
};

export const deleteExpertsListCache = (cache: any) => {
	deleteExpertsCache(cache);
};

/**
 * Parses vote count from content properties
 * Looks for the votes property and extracts the total count
 */
export function parseVoteCount(
	properties: QuestionListQuery_search_nodes_content_properties | null | undefined,
): number {
	if (!properties?.nodes) {
		return 0;
	}

	const votesProperty = properties.nodes.find((prop: any) => prop?.key === VOTES_PROPERTY_KEY);
	if (!votesProperty?.value) {
		return 0;
	}

	try {
		const votesData = JSON.parse(votesProperty.value);
		return votesData.total || 0;
	} catch {
		return 0;
	}
}

export function parseAnswerCount(
	children: QuestionListQuery_search_nodes_content_children | null | undefined,
): number {
	if (!children?.nodes) {
		return 0;
	}
	const count = children.nodes.length;
	return count;
}

export function parseAcceptedAnswer(properties: any): boolean {
	if (!properties?.nodes) {
		return false;
	}

	const acceptedAnswerProperty = properties.nodes.find(
		(prop: any) => prop.key === ACCEPTED_ANSWER_PROPERTY_KEY,
	);
	if (!acceptedAnswerProperty?.value) {
		return false;
	}

	try {
		const acceptedAnswerData = JSON.parse(acceptedAnswerProperty.value);
		return acceptedAnswerData.answer_id != null;
	} catch {
		return false;
	}
}

/**
 * Maps labels from different query formats to a consistent Topic format
 * Handles both formats:
 * 1. QuestionListQuery: labels { nodes: [{ name: string }] }
 * 2. QuestionsForSpaceQuery: labels: [{ label: string }]
 */
export function mapLabelsToTopics(labels: any): Array<Topic> {
	if (!labels) {
		return [];
	}

	if ('nodes' in labels) {
		// Handle QuestionListQuery format
		return labels.nodes.map((node: { name: string }) => ({
			name: node.name,
		}));
	}

	// Handle QuestionsForSpaceQuery format
	return labels.map((label: { label: string }) => ({
		name: label.label,
	}));
}

//TODO handle default values with graphql when it's implemented and remove this function
//https://product-fabric.atlassian.net/browse/CCECO-4231
export function mapQuestionDefaultValues(
	node: any,
	displayName?: string,
	spaceKey?: string,
): Question {
	if (!node) {
		return {
			content: {
				id: '',
				title: '',
				space: { key: '', name: '', alias: '' },
				history: { createdDate: '', createdBy: { displayName: '' } },
				answersTotalCount: 0,
				labels: [],
				numberOfVotes: 0,
				hasAcceptedAnswer: false,
			},
		};
	}

	return {
		content: {
			id: node.id ?? '',
			title: node.title ?? '',
			labels: mapLabelsToTopics(node.labels),
			space: {
				key: node.space?.key ?? spaceKey ?? '',
				name: node.space?.name ?? '',
				alias: node.space?.alias ?? '',
			},
			history: {
				createdDate: node.latestVersion?.createdAt ?? node.history?.createdDate ?? '',
				createdBy: {
					displayName: node.history?.createdBy?.displayName ?? displayName ?? '',
				},
			},
			answersTotalCount: node.answersTotalCount ?? parseAnswerCount(node.children) ?? 0,
			numberOfVotes: node.voteProperties?.total ?? parseVoteCount(node.properties),
			hasAcceptedAnswer: node.hasAcceptedAnswer ?? parseAcceptedAnswer(node.properties),
		},
	};
}
